<template>
    <div id="credit-statement-page">
        <div class="container-fluid py-4">
            <div class="d-flex flex-wrap justify-content-between align-items-center gap-md mb-4 header-toolbar">
                <div class="d-flex flex-column align-items-start">
                    <KvButton 
                        variant="outlined" 
                        size="small"
                        prepend-icon="ui-arrow-left"
                        @click="goBack"
                        class="mb-2"
                    >
                        {{ t('balance-back_to_credits') }}
                    </KvButton>
                    <h1 class="kv-title mb-1">{{ t('balance-credit_statement_title') }}</h1>
                    <p class="text-muted mb-0">{{ t('balance-credit_statement_subtitle') }}</p>
                </div>

                <div class="d-flex align-items-center gap-sm flex-wrap filter-controls">
                    <div class="d-flex align-items-center period-selector-group">
                        <span class="filter-label mb-0 text-muted mr-2">{{ t('balance-period_colon') }}</span>
                        <KvSelect 
                            v-model="selectedMonth" 
                            :options="periodOptions" 
                            @change="selectMonth"
                            :placeholder="t('balance-select_month_placeholder')"
                            variant="outlined"
                            class="kv-select select-month"
                        />
                    </div>
                    <KvButton 
                        variant="outlined" 
                        prepend-icon="ui-download-01" 
                        @click="downloadCsv"
                        :loading="downloadingCsv"
                        :disabled="!selectedMonth"
                        class="btn-download-csv"
                    >
                        {{ t('balance-download_csv') }}
                    </KvButton>
                </div>
            </div>

            <loader v-if="loadingMonths" />

            <div v-else-if="monthsError" class="alert alert-danger mb-4">
                {{ t('balance-load_periods_error') }}
            </div>

            <div v-else-if="!availableMonths.length" class="balance-card text-center py-5">
                <div class="empty-icon-circle mb-3 mx-auto">
                    <Icon icon-class="ui-search-01" class="text-muted display-4" size="8" />
                </div>
                <p class="text-muted mb-0">{{ t('balance-no_statement_available') }}</p>
            </div>

            <template v-else>
                <div class="balance-card p-0 overflow-hidden">
                    <div class="card-header-table px-4 py-3">
                        <h3 class="card-title mb-0">{{ t('balance-usage_details') }}</h3>
                    </div>

                    <div class="table-container">
                        <Grid 
                            v-if="apiFilters"
                            source="transactions/credits-extract" 
                            :filters="apiFilters" 
                            :showSearch="false" 
                            :quantityPerPage="50"
                            :displayItemsPerPage="true"
                            class="credit-statement-grid"
                        >
                            <template slot="rows" slot-scope="props">
                                <thead>
                                    <tr>
                                        <th style="width: 15%; min-width: 120px;">{{ t('balance-date') }}</th>
                                        <th style="width: 35%; min-width: 260px;">{{ t('balance-action') }}</th>
                                        <th style="width: 12%; min-width: 110px;">{{ t('balance-credit_type') }}</th>
                                        <th style="width: 14%; min-width: 130px;">{{ t('balance-linked_client_id') }}</th>
                                        <th style="width: 12%; min-width: 110px;">{{ t('balance-execution_id') }}</th>
                                        <th class="text-right" style="width: 12%; min-width: 110px;">{{ t('balance-consumed_value') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in props.data" :key="item.id" class="credit-statement-row">
                                        <td>
                                            <div class="d-flex flex-column">
                                                <span class="date-main">
                                                    <human-date :date="item.date" format="[d]/[M]/[Y]" />
                                                </span>
                                                <small class="time-sub text-muted">
                                                    <human-date :date="item.date" format="[h]:[m]:[s]" />
                                                </small>
                                            </div>
                                        </td>

                                        <td>
                                            <div class="d-flex align-items-center">
                                                <div class="action-icon-box mr-3">
                                                    <Icon :icon-class="getActionIcon(item)" size="4" />
                                                </div>
                                                <div class="d-flex flex-column min-w-0">
                                                    <span class="action-title text-truncate" :title="item.automation_title">
                                                        {{ item.automation_title }}
                                                    </span>
                                                    <small class="action-subtitle text-muted">
                                                        {{ formatActionType(item.action_type) }}
                                                    </small>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <span class="kv-badge">
                                                {{ item.automation_type === 'automation' ? t('balance-automation') : (item.automation_type === 'campaign' ? t('balance-campaign') : t('balance-action_type_other')) }}
                                            </span>
                                        </td>

                                        <td>
                                            <router-link v-if="item.clients_id" :to="`/client/${item.clients_id}`" class="client-link" :title="item.clients_id">
                                                <span class="badge-client d-inline-flex align-items-center">
                                                    <Icon icon-class="ui-user-01" class="mr-1" size="3" />
                                                    {{ formatClientId(item.clients_id) }}
                                                </span>
                                            </router-link>
                                            <span v-else class="text-muted empty-dash">—</span>
                                        </td>

                                        <td>
                                            <span class="execution-tag" :title="item.id" @click="copyExecutionId(item.id)">
                                                #{{ formatExecutionId(item.id) }}
                                            </span>
                                        </td>

                                        <td class="text-right">
                                            <span class="amount-value">
                                                -{{ formatNumber(item.amount, 2) }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="!props.data.length" class="empty-row">
                                        <td colspan="6">
                                            <div class="empty-state text-center py-5">
                                                <div class="empty-icon-circle mb-3">
                                                    <Icon icon-class="ui-search-01" class="text-muted display-4" size="8" />
                                                </div>
                                                <p class="text-muted">{{ t('balance-no_transactions_for_month').replace('{month}', selectedMonthLabel) }}</p>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                            <template slot="list" slot-scope="props">
                                <div class="statement-card-list">
                                    <div 
                                        v-for="item in props.data" 
                                        :key="item.id" 
                                        class="statement-card-item"
                                    >
                                        <div class="d-flex justify-content-between align-items-start mb-2">
                                            <div class="d-flex flex-column">
                                                <span class="date-main">
                                                    <human-date :date="item.date" format="[d]/[M]/[Y]" />
                                                </span>
                                                <small class="time-sub text-muted">
                                                    <human-date :date="item.date" format="[h]:[m]:[s]" />
                                                </small>
                                            </div>
                                            <span class="amount-value text-danger font-weight-600">
                                                -{{ formatNumber(item.amount, 2) }}
                                            </span>
                                        </div>

                                        <div class="d-flex align-items-center mb-2">
                                            <div class="action-icon-box mr-2">
                                                <Icon :icon-class="getActionIcon(item)" size="4" />
                                            </div>
                                            <div class="d-flex flex-column min-w-0 flex-grow-1 overflow-hidden">
                                                <span class="action-title text-truncate font-weight-500" :title="item.automation_title">
                                                    {{ item.automation_title }}
                                                </span>
                                                <small class="action-subtitle text-muted">
                                                    {{ formatActionType(item.action_type) }}
                                                </small>
                                            </div>
                                        </div>

                                        <div class="d-flex align-items-center flex-wrap pt-2 statement-card-footer">
                                            <span class="kv-badge">
                                                {{ item.automation_type === 'automation' ? t('balance-automation') : (item.automation_type === 'campaign' ? t('balance-campaign') : t('balance-action_type_other')) }}
                                            </span>

                                            <router-link v-if="item.clients_id" :to="`/client/${item.clients_id}`" class="client-link" :title="item.clients_id">
                                                <span class="badge-client d-inline-flex align-items-center">
                                                    <Icon icon-class="ui-user-01" class="mr-1" size="3" />
                                                    {{ formatClientId(item.clients_id) }}
                                                </span>
                                            </router-link>

                                            <span class="execution-tag ml-auto" :title="item.id" @click="copyExecutionId(item.id)">
                                                #{{ formatExecutionId(item.id) }}
                                            </span>
                                        </div>
                                    </div>

                                    <div v-if="!props.data.length" class="empty-state text-center py-5">
                                        <div class="empty-icon-circle mb-3">
                                            <Icon icon-class="ui-search-01" class="text-muted display-4" size="8" />
                                        </div>
                                        <p class="text-muted">{{ t('balance-no_transactions_for_month').replace('{month}', selectedMonthLabel) }}</p>
                                    </div>
                                </div>
                            </template>
                        </Grid>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import KvButton from "../components/form/kv-button.vue";
import HumanDate from "../components/human-date/human-date.vue";
import FormatMixin from "../balance-v2/mixins/format-mixin.js";
import Grid from "../components/grid/grid.vue";
import Icon from "../components/icon/icon.vue";
import KvSelect from "../components/inputs/kv-select.vue";
import { getAutomationIcon } from "../balance-v2/utils/automation-icons.js";
import keviApi from "../keviApi.js";

export default {
    mixins: [FormatMixin],
    components: { KvButton, HumanDate, Grid, Icon, KvSelect },
    data() {
        return {
            loadingMonths: true,
            monthsError: false,
            downloadingCsv: false,
            selectedMonth: null,
            availableMonths: [],
            apiFilters: null
        };
    },
    async created() {
        await this.fetchAvailableMonths();
    },
    computed: {
        periodOptions() {
            const localeMap = { pt: 'pt-br', en: 'en', es: 'es' };
            const currentLocale = localeMap[this.$root.lang] || 'pt-br';
            const formatStr = this.$root.lang === 'en' ? 'MMMM YYYY' : 'MMMM [de] YYYY';
            return this.availableMonths.map(monthStr => {
                let label = moment(monthStr, 'YYYY-MM').locale(currentLocale).format(formatStr);
                label = label.charAt(0).toUpperCase() + label.slice(1);
                return {
                    value: monthStr,
                    label: label
                };
            });
        },
        selectedMonthLabel() {
            if (!this.selectedMonth) return '';
            const localeMap = { pt: 'pt-br', en: 'en', es: 'es' };
            const currentLocale = localeMap[this.$root.lang] || 'pt-br';
            const formatStr = this.$root.lang === 'en' ? 'MMMM YYYY' : 'MMMM [de] YYYY';
            let label = moment(this.selectedMonth, 'YYYY-MM').locale(currentLocale).format(formatStr);
            return label.charAt(0).toUpperCase() + label.slice(1);
        }
    },
    methods: {
        goBack() {
            if (window.history.length > 1) {
                this.$router.back();
            } else {
                this.$router.push('/balance');
            }
        },
        async fetchAvailableMonths() {
            this.loadingMonths = true;
            this.monthsError = false;
            try {
                const response = await this.$http(keviApi.getOptions('transactions/credits-extract-months', 'GET'));
                if (response && response.status === 200 && response.body.data) {
                    this.availableMonths = response.body.data;
                    if (this.availableMonths.length) {
                        this.selectedMonth = this.availableMonths[0];
                        this.updateFilters();
                    }
                }
            } catch (err) {
                console.error("Erro ao buscar meses disponíveis para extrato", err);
                this.monthsError = true;
            } finally {
                this.loadingMonths = false;
            }
        },
        selectMonth() {
            this.updateFilters();
        },
        updateFilters() {
            const startDate = moment(this.selectedMonth, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
            const endDate = moment(this.selectedMonth, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');
            this.apiFilters = {
                period: { start: startDate, end: endDate }
            };
        },
        getActionIcon(item) {
            const action = (item.action_type || '').toLowerCase();
            if (action.includes('whatsapp')) return 'ui-message-chat-square';
            if (action.includes('email')) return 'ui-mail-01';
            if (action.includes('rcs')) return 'ui-message-notification-square';
            if (action.includes('sms')) return 'ui-message-text-square-01';
            if (action.includes('webhook')) return 'ui-link-01';
            if (action.includes('ai') || action.includes('ia')) return 'ui-stars-02';
            if (action.includes('task') || action.includes('tarefa')) return 'ui-file-check-02';
            return getAutomationIcon(item, 'trigger_type');
        },
        formatClientId(id) {
            if (!id) return '—';
            const str = String(id);
            if (str.length > 10) {
                return `#...${str.slice(-4)}`;
            }
            return `#${str}`;
        },
        formatExecutionId(id) {
            if (!id) return '—';
            const str = String(id);
            if (str.length > 8) {
                return `...${str.slice(-4)}`;
            }
            return str;
        },
        copyExecutionId(id) {
            if (!id) return;
            navigator.clipboard.writeText(String(id)).then(() => {
                this.$store.dispatch('notification/success', this.t('common-copied'));
            }).catch(() => {});
        },
        async downloadCsv() {
            if (!this.selectedMonth || this.downloadingCsv) return;
            this.downloadingCsv = true;
            try {
                const startDate = moment(this.selectedMonth, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
                const endDate = moment(this.selectedMonth, 'YYYY-MM').endOf('month').format('YYYY-MM-DD');
                const filters = JSON.stringify({ period: { start: startDate, end: endDate } });
                
                const infoUrl = `transactions/export-csv-info?filters=${encodeURIComponent(filters)}`;
                const infoRes = await this.$http(keviApi.getOptions(infoUrl, 'GET'));
                
                if (infoRes && infoRes.status === 200) {
                    const { chunks } = infoRes.body;
                    for (let i = 0; i < chunks; i++) {
                        const csvUrl = `transactions/export-csv?filters=${encodeURIComponent(filters)}&chunk=${i}`;
                        const csvRes = await this.$http(keviApi.getOptions(csvUrl, 'GET'));
                        
                        if (csvRes && csvRes.status === 200) {
                            const blob = new Blob([csvRes.body], { type: 'text/csv;charset=utf-8;' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            const monthLabel = moment(this.selectedMonth, 'YYYY-MM').format('YYYY-MM');
                            link.href = url;
                            link.setAttribute('download', `credit-statement-${monthLabel}${chunks > 1 ? '-parte-' + (i + 1) : ''}.csv`);
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                        }
                    }
                }
            } catch (err) {
                console.error("Erro ao baixar CSV:", err);
                this.$store.dispatch('notification/error', this.t('balance-prepare_csv_error'));
            } finally {
                this.downloadingCsv = false;
            }
        }
    }
};
</script>

<style scoped lang="scss">
#credit-statement-page {
    margin: 0 20px 20px 20px;
    min-height: calc(100vh - 80px);

    @media (max-width: 768px) {
        margin: 0 10px 10px 10px;
        
        .container-fluid {
            padding-left: 10px;
            padding-right: 10px;
        }
    }
}

.kv-title {
    font-size: 26px;
    font-weight: 500;
    color: #111827;

    @media (max-width: 576px) {
        font-size: 22px;
    }
}

.gap-md { gap: 16px; }
.gap-sm { gap: 8px; }

.header-toolbar {
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start !important;
    }
}


.filter-controls {
    @media (max-width: 768px) {
        width: 100%;
        justify-content: space-between;
    }

    @media (max-width: 576px) {
        flex-direction: column;
        align-items: stretch !important;
        gap: 12px;
    }
}

.period-selector-group {
    @media (max-width: 576px) {
        width: 100%;
        justify-content: space-between;
    }
}

.select-month {
    width: 220px;
    background: white;

    @media (max-width: 576px) {
        flex: 1;
        width: auto !important;
    }
}

.btn-download-csv {
    @media (max-width: 576px) {
        width: 100%;
        justify-content: center;
    }
}

.filter-label {
    font-size: 13px;
    font-weight: 500;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.balance-card {
    background-color: #fff;
    border: 1px solid #E2E8F0;
    border-radius: 16px;
    padding: 20px 24px;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.02);

    @media (max-width: 768px) {
        padding: 16px;
    }
}

.card-header-table {
    border-bottom: 1px solid #E2E8F0;
}

.card-title {
    font-size: 16px;
    font-weight: 500;
    color: #111827;
}

.table-container {
    :deep(.grid-header) {
        display: none;
    }

    :deep(.table-wrapper) {
        border-radius: 0;
        border: none;

        &:before {
            display: none;
        }
    }

    :deep(.table-responsive) {
        border-radius: 0;
        background: transparent;
    }

    :deep(.kv-table-footer) {
        padding: 14px 20px;
        margin-top: 0;

        @media (max-width: 576px) {
            padding: 12px 14px;
            flex-direction: column;
            gap: 12px;
            align-items: center;
        }
    }

    :deep(table) {
        margin-bottom: 0;
        min-width: 850px;
        
        thead {
            background-color: #F9FAFB;
            
            th {
                color: #6B7280 !important;
                font-weight: 500;
                text-transform: uppercase;
                font-size: 12px;
                letter-spacing: 0.05em;
                padding: 14px 20px;
                border: none;
                border-bottom: 1px solid #E5E7EB;
                line-height: 1.2;
                white-space: nowrap;
            }
        }
        
        tbody {
            tr {
                transition: background-color 0.15s;
                
                &:hover td {
                    background-color: #F8FAFC !important;
                }
                
                td {
                    padding: 14px 20px;
                    border-bottom: 1px solid #F3F4F6;
                    vertical-align: middle;
                    color: #374151;
                    background-color: #FFFFFF;
                }
            }
        }
    }
}

.date-main {
    font-size: 13px;
    font-weight: 500;
    color: #111827;
}

.time-sub {
    font-size: 12px;
}

.action-icon-box {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3F4F6;
    color: #6B7280;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.action-title {
    font-size: 13px;
    font-weight: 500;
    color: #111827;
    max-width: 540px;
}

.action-subtitle {
    font-size: 12px;
}

.kv-badge {
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    display: inline-block;
    white-space: nowrap;
    background: #F3F4F6;
    color: #4B5563;
    border: 1px solid #E5E7EB;
}

.badge-client {
    background: #F9FAFB;
    color: #4B5563;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    transition: all 0.15s ease;
    border: 1px solid #E5E7EB;

    .ui-icon {
        color: #9CA3AF;
    }
}

.client-link {
    text-decoration: none !important;

    &:hover .badge-client {
        background: #F3F4F6;
        color: #111827;
        border-color: #D1D5DB;

        .ui-icon {
            color: #4B5563;
        }
    }
}

.execution-tag {
    font-family: "Inter", sans-serif;
    font-size: 12px;
    color: #6B7280;
    background: #F9FAFB;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid #E5E7EB;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: #F3F4F6;
        color: #111827;
        border-color: #D1D5DB;
    }
}

.amount-value {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
}

.empty-state {
    .empty-icon-circle {
        width: 64px;
        height: 64px;
        background: #F3F4F6;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto;
        color: #9CA3AF;
    }
}

.empty-icon-circle {
    width: 64px;
    height: 64px;
    background: #F3F4F6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9CA3AF;
}

.statement-card-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
    background: #F9FAFB;

    @media (max-width: 576px) {
        padding: 12px 8px;
        gap: 8px;
    }
}

.statement-card-item {
    background: #FFFFFF;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    transition: background 0.15s ease;

    &:hover {
        background-color: #FAFAFA;
    }
}

.statement-card-footer {
    border-top: 1px dashed #F1F5F9;
    gap: 6px;
}
</style>
